import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Save, X, ChevronsUpDown, Search } from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { Skeleton } from '@/components/ui/skeleton';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { formatDateTime } from '@/lib/utils';
import { useDebounce } from '@/hooks/useDebounce';
import { useSession } from '@/hooks/useSession';
import { hasAnyRole } from '@/types/session';
import { MultiSelectPopover } from '@/shared/components/MultiSelectPopover';
import {
  archiveAccount,
  getAccount,
  unarchiveAccount,
  updateAccountSettings,
  updateAccountStatus,
  getAccountUsergroups,
  getAllUsergroups,
  assignAccountUsergroups,
  searchOrganisations,
  getOrganisationConfigFromPostgres,
  getLegacyHardwareOptions,
  updateLegacyOrganisationConfig,
} from '../api';
import { getAccountStatusPresentation } from '../lib/status';
import type { AccountUsergroup } from '../api';
import type { LegacyHardwareOption, LegacyOrganisationConfig } from '../types';
import { BillingCard } from './BillingCard';
import AllocationCard from './AllocationCard';

interface SettingsTabProps {
  accountId: string;
}

function formatOrganisationLabel(name: string, accountNumber?: string | null) {
  if (!accountNumber) return name;

  const normalizedNumber = accountNumber.replace(/^A-/, '').split('-')[0];
  return normalizedNumber ? `#${normalizedNumber} - ${name}` : name;
}

const LEGACY_FEATURE_FLAG_ORDER = [
  'loginExponentialBackoff',
  'usergroupMigration',
  'chartGapSupport',
  'uncertaintyAwareRules',
  'automaticReports',
  'allowMultipointCalibration',
  'enclosureMaps',
  'calibrationReminders',
  'customerManagedCalibration',
  'notificationSchedule',
  'dailyLogs',
  'auditorRole',
  'forceCommentAlert',
  'requireCredentialToAck',
  // The AI connector (Claude, ChatGPT): off unless ticked here, checked on every call.
  'aiConnector',
  // Lets the connector acknowledge alarms; never applies while requireCredentialToAck is on.
  'aiConnectorAcknowledge',
] as const;

function normalizeLegacyConfigForComparison(config: LegacyOrganisationConfig) {
  return {
    sensor_hardwares: [...config.sensor_hardwares].sort(),
    connectivities: [...config.connectivities].sort(),
    description: config.description || '',
    default_config_tags: [...config.default_config_tags].sort(),
    featureFlags: Object.fromEntries(
      Object.entries(config.featureFlags).sort(([left], [right]) => left.localeCompare(right))
    ),
  };
}

function legacyConfigEquals(left: LegacyOrganisationConfig, right: LegacyOrganisationConfig) {
  return JSON.stringify(normalizeLegacyConfigForComparison(left)) === JSON.stringify(
    normalizeLegacyConfigForComparison(right)
  );
}

export default function SettingsTab({ accountId }: SettingsTabProps) {
  const queryClient = useQueryClient();
  const { user } = useSession();

  const { data: account, isLoading } = useQuery({
    queryKey: ['account', accountId],
    queryFn: () => getAccount(accountId, { includeArchived: true }),
  });
  const canArchiveAccount = hasAnyRole(user, ['director', 'full_admin']);

  const [gdriveLink, setGdriveLink] = useState('');
  const [hubspotId, setHubspotId] = useState('');
  const [quickbooksId, setQuickbooksId] = useState('');
  const [alertVoiceProvider, setAlertVoiceProvider] = useState<'twilio' | 'telnyx'>('twilio');

  // Sync form state when data loads
  useEffect(() => {
    if (account) {
      setGdriveLink(account.gdrive_root_folder_id || '');
      setHubspotId(account.hubspot_company_id || '');
      setQuickbooksId(account.quickbooks_customer_id || '');
      setAlertVoiceProvider(account.alert_voice_provider || 'twilio');
    }
  }, [account]);

  const mutation = useMutation({
    mutationFn: (settings: {
      hubspotCompanyId?: string | null;
      quickbooksCustomerId?: string | null;
      gdriveRootFolderId?: string | null;
      alertVoiceProvider?: 'twilio' | 'telnyx';
    }) => updateAccountSettings(accountId, settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account', accountId] });
      queryClient.invalidateQueries({ queryKey: ['account-dashboard', accountId] });
      toast.success('Settings saved');
    },
    onError: () => {
      toast.error('Failed to save settings');
    },
  });

  const archiveMutation = useMutation({
    mutationFn: () => archiveAccount(accountId),
    onSuccess: (updatedAccount) => {
      queryClient.setQueryData(['account', accountId], updatedAccount);
      queryClient.setQueryData(['account-dashboard', accountId], (current: { account?: Record<string, unknown> } | undefined) => (
        current
          ? {
              ...current,
              account: {
                ...current.account,
                archived_at: updatedAccount.archived_at,
                archived_by: updatedAccount.archived_by,
              },
            }
          : current
      ));
      // Only invalidate the account list — the detail view is already updated
      // via setQueryData. A bare invalidateQueries() refetches EVERY query in
      // the app (245 accounts, dashboards, deals, etc.) and made archive feel
      // multi-second slow.
      queryClient.invalidateQueries({ queryKey: ['accounts-table'] });
      toast.success('Account archived');
    },
    onError: () => {
      toast.error('Failed to archive account');
    },
  });

  const statusMutation = useMutation({
    mutationFn: (newStatus: 'active' | 'suspended') => updateAccountStatus(accountId, newStatus),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account', accountId] });
      queryClient.invalidateQueries({ queryKey: ['account-dashboard', accountId] });
      toast.success('Account status updated');
    },
    onError: () => {
      toast.error('Failed to update account status');
    },
  });

  const unarchiveMutation = useMutation({
    mutationFn: () => unarchiveAccount(accountId),
    onSuccess: (updatedAccount) => {
      queryClient.setQueryData(['account', accountId], updatedAccount);
      queryClient.setQueryData(['account-dashboard', accountId], (current: { account?: Record<string, unknown> } | undefined) => (
        current
          ? {
              ...current,
              account: {
                ...current.account,
                archived_at: updatedAccount.archived_at,
                archived_by: updatedAccount.archived_by,
              },
            }
          : current
      ));
      // Same targeted invalidation as archive — see comment there.
      queryClient.invalidateQueries({ queryKey: ['accounts-table'] });
      toast.success('Account unarchived');
    },
    onError: () => {
      toast.error('Failed to unarchive account');
    },
  });

  const handleSave = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    mutation.mutate({
      gdriveRootFolderId: gdriveLink.trim() || null,
      hubspotCompanyId: hubspotId.trim() || null,
      quickbooksCustomerId: quickbooksId.trim() || null,
      alertVoiceProvider,
    });
  };

  const isDirty =
    account &&
    (gdriveLink !== (account.gdrive_root_folder_id || '') ||
      hubspotId !== (account.hubspot_company_id || '') ||
      quickbooksId !== (account.quickbooks_customer_id || '') ||
      alertVoiceProvider !== (account.alert_voice_provider || 'twilio'));

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!account?.archived_at && (
        <>
          <AllocationCard accountId={accountId} />
          <OrganisationCard accountId={accountId} />
          <BillingCard accountId={accountId} />
          <UsergroupsCard accountId={accountId} />
          <LegacyOrganisationConfigCard accountId={accountId} />
        </>
      )}

      {!account?.archived_at && (
        <form onSubmit={handleSave}>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">External Integrations</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-1.5">
                <Label htmlFor="alert-voice-provider">Voice Alert Provider</Label>
                <Select
                  value={alertVoiceProvider}
                  onValueChange={(value) => {
                    if (value === 'twilio' || value === 'telnyx') setAlertVoiceProvider(value);
                  }}
                >
                  <SelectTrigger id="alert-voice-provider" aria-label="Voice Alert Provider">
                    <SelectValue>
                      {alertVoiceProvider === 'telnyx' ? 'Telnyx' : 'Twilio (default)'}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="twilio">Twilio (default)</SelectItem>
                    <SelectItem value="telnyx">Telnyx</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Applies to spoken phone calls and numeric pager alerts. Telnyx is used only when explicitly selected.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="gdrive-link">Google Drive Link</Label>
                <div className="flex gap-2">
                  <Input
                    id="gdrive-link"
                    placeholder="https://drive.google.com/drive/folders/..."
                    value={gdriveLink}
                    onChange={(e) => setGdriveLink(e.target.value)}
                  />
                  {gdriveLink && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      asChild
                    >
                      <a href={gdriveLink} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Link to the account&apos;s Google Drive folder
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="hubspot-id">HubSpot Company ID</Label>
                <div className="flex gap-2">
                  <Input
                    id="hubspot-id"
                    placeholder="e.g. 12345678"
                    value={hubspotId}
                    onChange={(e) => setHubspotId(e.target.value)}
                  />
                  {hubspotId && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      asChild
                    >
                      <a
                        href={`https://app.hubspot.com/contacts/6893890/company/${hubspotId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  HubSpot company record ID for CRM integration
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="quickbooks-id">QuickBooks Customer ID</Label>
                <Input
                  id="quickbooks-id"
                  placeholder="e.g. 123"
                  value={quickbooksId}
                  onChange={(e) => setQuickbooksId(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  QuickBooks customer ID for accounting integration
                </p>
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  type="submit"
                  disabled={mutation.isPending || !isDirty}
                  size="sm"
                >
                  <Save className="mr-1.5 h-4 w-4" />
                  {mutation.isPending ? 'Saving...' : 'Save Settings'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>
      )}

      {!account?.archived_at && account && (() => {
        const statusPresentation = getAccountStatusPresentation(account.status);
        return (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Account Status</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Current status:{' '}
                <Badge variant={statusPresentation.badgeVariant}>{account.status}</Badge>
              </p>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={statusMutation.isPending}
                    className={statusPresentation.actionClassName}
                  >
                    {statusMutation.isPending ? 'Updating...' : statusPresentation.actionLabel}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{statusPresentation.confirmTitle}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {statusPresentation.confirmDescription}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => statusMutation.mutate(statusPresentation.nextStatus)}
                    >
                      {statusPresentation.actionVerb}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </CardContent>
          </Card>
        );
      })()}

      {canArchiveAccount && account && (
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="text-base text-red-700">Danger Zone</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Archived accounts are hidden from account-backed pages across the admin UI until unarchived.
            </p>
            {account.archived_at ? (
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-muted-foreground">
                  Archived on {formatDateTime(account.archived_at)}
                  {account.archived_by_name ? ` by ${account.archived_by_name}` : ''}.
                </p>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={unarchiveMutation.isPending}
                    >
                      {unarchiveMutation.isPending ? 'Unarchiving...' : 'Unarchive Account'}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Unarchive Account</AlertDialogTitle>
                      <AlertDialogDescription>
                        This account and its linked entities will become visible again on admin pages immediately.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => unarchiveMutation.mutate()}>
                        Unarchive
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            ) : (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    type="button"
                    variant="destructive"
                    disabled={archiveMutation.isPending}
                  >
                    {archiveMutation.isPending ? 'Archiving...' : 'Archive Account'}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Archive Account</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will hide the account and its linked entities from account-backed V2 pages. V1 pages will still show the account. MongoDB data will not be changed.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-red-600 hover:bg-red-700"
                      onClick={() => archiveMutation.mutate()}
                    >
                      Archive
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function TagArrayInput({
  value,
  onChange,
  placeholder = 'Type and press Enter',
  disabled = false,
}: {
  value: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  const [inputValue, setInputValue] = useState('');

  const addTag = (tag: string) => {
    const trimmed = tag.trim();
    if (!trimmed || value.includes(trimmed)) {
      setInputValue('');
      return;
    }
    onChange([...value, trimmed]);
    setInputValue('');
  };

  const removeTag = (tagToRemove: string) => {
    onChange(value.filter((tag) => tag !== tagToRemove));
  };

  return (
    <div className="flex min-h-10 flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-3 py-2">
      {value.map((tag) => (
        <Badge key={tag} variant="secondary" className="gap-1 text-xs font-normal">
          {tag}
          {!disabled && (
            <button
              type="button"
              onClick={() => removeTag(tag)}
              className="rounded-full hover:bg-muted-foreground/20"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </Badge>
      ))}
      <Input
        value={inputValue}
        onChange={(event) => setInputValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            addTag(inputValue);
          } else if (event.key === 'Backspace' && !inputValue && value.length > 0) {
            removeTag(value[value.length - 1]);
          }
        }}
        onBlur={() => {
          if (inputValue.trim()) addTag(inputValue);
        }}
        placeholder={value.length === 0 ? placeholder : ''}
        disabled={disabled}
        className="h-auto min-w-[120px] flex-1 border-0 p-0 text-sm shadow-none focus-visible:ring-0"
      />
    </div>
  );
}

function LegacyOrganisationConfigCard({ accountId }: { accountId: string }) {
  const queryClient = useQueryClient();

  const { data: account } = useQuery({
    queryKey: ['account', accountId],
    queryFn: () => getAccount(accountId, { includeArchived: true }),
  });

  const mongoOrganisationId = account?.mongo_organisation_id || null;

  const { data: organisationConfig, isLoading: organisationConfigLoading } = useQuery({
    queryKey: ['organisation-config', mongoOrganisationId],
    queryFn: () => getOrganisationConfigFromPostgres(mongoOrganisationId!),
    enabled: Boolean(mongoOrganisationId),
  });

  const { data: hardwareOptions, isLoading: hardwareOptionsLoading } = useQuery({
    queryKey: ['legacy-organisation-hardware-options'],
    queryFn: async () => {
      const [sensorHardwares, connectivities] = await Promise.all([
        getLegacyHardwareOptions('module'),
        getLegacyHardwareOptions('connectivity'),
      ]);

      return { sensorHardwares, connectivities };
    },
    enabled: Boolean(mongoOrganisationId),
  });

  const [formState, setFormState] = useState<LegacyOrganisationConfig | null>(null);

  useEffect(() => {
    if (organisationConfig) {
      setFormState(organisationConfig);
    } else if (!mongoOrganisationId) {
      setFormState(null);
    }
  }, [organisationConfig, mongoOrganisationId]);

  const featureFlagKeys = useMemo(() => {
    const existingKeys = formState ? Object.keys(formState.featureFlags) : [];
    const extraKeys = existingKeys
      .filter((key) => !LEGACY_FEATURE_FLAG_ORDER.includes(key as typeof LEGACY_FEATURE_FLAG_ORDER[number]))
      .sort((left, right) => left.localeCompare(right));

    return [...LEGACY_FEATURE_FLAG_ORDER, ...extraKeys];
  }, [formState]);

  const mutation = useMutation({
    mutationFn: async (nextConfig: LegacyOrganisationConfig) => {
      if (!mongoOrganisationId) {
        throw new Error('Legacy organisation not linked');
      }

      return updateLegacyOrganisationConfig(mongoOrganisationId, {
        sensor_hardwares: nextConfig.sensor_hardwares,
        connectivities: nextConfig.connectivities,
        description: nextConfig.description,
        default_config_tags: nextConfig.default_config_tags,
        featureFlags: nextConfig.featureFlags,
      });
    },
    onSuccess: (result) => {
      if (mongoOrganisationId && result.synced && result.organisation) {
        queryClient.setQueryData(['organisation-config', mongoOrganisationId], result.organisation);
      }
      queryClient.invalidateQueries({ queryKey: ['account', accountId] });
      queryClient.invalidateQueries({ queryKey: ['account-dashboard', accountId] });

      if (result.synced) {
        toast.success('Legacy organisation settings saved');
      } else {
        toast.warning('Saved to MongoDB. PostgreSQL will update via CDC shortly.');
      }
    },
    onError: (error) => {
      const description = error instanceof Error ? error.message : undefined;
      toast.error('Failed to save legacy organisation settings', { description });
    },
  });

  if (!mongoOrganisationId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Legacy Organisation Config</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Link a legacy organisation above before editing MongoDB-backed organisation settings.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (organisationConfigLoading || hardwareOptionsLoading || !formState || !hardwareOptions) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Legacy Organisation Config</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-36 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-56 w-full" />
        </CardContent>
      </Card>
    );
  }

  const isCustomerOrganisation = formState.tags.includes('customer');
  const isDirty = organisationConfig ? !legacyConfigEquals(formState, organisationConfig) : false;
  const moduleOptions = hardwareOptions.sensorHardwares.map((hardware: LegacyHardwareOption) => ({
    id: hardware._id,
    label: `${hardware.name} (${hardware.hex_id})`,
  }));
  const connectivityOptions = hardwareOptions.connectivities.map((hardware: LegacyHardwareOption) => ({
    id: hardware._id,
    label: `${hardware.name} (${hardware.hex_id})`,
  }));

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        mutation.mutate(formState);
      }}
    >
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Legacy Organisation Config</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <p className="text-xs text-muted-foreground">
            This form loads from PostgreSQL. Saves go to the legacy MongoDB organisation first, then return here through CDC.
          </p>

          {isCustomerOrganisation ? (
            <>
              <div className="space-y-2">
                <Label>Sensor Modules</Label>
                <MultiSelectPopover
                  value={formState.sensor_hardwares}
                  options={moduleOptions}
                  onChange={(sensor_hardwares) => {
                    setFormState((current) => (current ? { ...current, sensor_hardwares } : current));
                  }}
                  placeholder="Select sensor modules..."
                  searchPlaceholder="Search modules..."
                  emptyMessage="No sensor modules found"
                  noMatchesMessage="No matching modules"
                  disabled={mutation.isPending}
                />
                <p className="text-xs text-muted-foreground">
                  These modules appear in the legacy configurator for this organisation.
                </p>
              </div>

              <div className="space-y-2">
                <Label>Connectivities</Label>
                <MultiSelectPopover
                  value={formState.connectivities}
                  options={connectivityOptions}
                  onChange={(connectivities) => {
                    setFormState((current) => (current ? { ...current, connectivities } : current));
                  }}
                  placeholder="Select connectivities..."
                  searchPlaceholder="Search connectivities..."
                  emptyMessage="No connectivities found"
                  noMatchesMessage="No matching connectivities"
                  disabled={mutation.isPending}
                />
                <p className="text-xs text-muted-foreground">
                  These connectivity options appear in the legacy configurator for this organisation.
                </p>
              </div>
            </>
          ) : (
            <p className="text-xs text-muted-foreground">
              Module and connectivity filters only apply to customer organisations.
            </p>
          )}

          <div className="space-y-2">
            <Label>Description</Label>
            <RichTextEditor
              value={formState.description}
              onChange={(description) => {
                setFormState((current) => (current ? { ...current, description } : current));
              }}
              placeholder="Organisation notes and description..."
              disabled={mutation.isPending}
              minHeight="180px"
            />
          </div>

          <div className="space-y-2">
            <Label>Default User Config Tags</Label>
            <TagArrayInput
              value={formState.default_config_tags}
              onChange={(default_config_tags) => {
                setFormState((current) => (current ? { ...current, default_config_tags } : current));
              }}
              placeholder="Type a config tag and press Enter"
              disabled={mutation.isPending}
            />
            <p className="text-xs text-muted-foreground">
              New users in this organisation inherit these config tags.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Feature Flags</Label>
            <div className="overflow-hidden rounded-md border">
              <div className="grid grid-cols-[1fr_auto] gap-3 border-b bg-muted/50 px-3 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                <span>Flag Name</span>
                <span>Enabled</span>
              </div>
              <div className="divide-y">
                {featureFlagKeys.map((flagName) => (
                  <label
                    key={flagName}
                    className="grid cursor-pointer grid-cols-[1fr_auto] items-center gap-3 px-3 py-2 text-sm"
                  >
                    <span className="break-all">{flagName}</span>
                    <Checkbox
                      checked={Boolean(formState.featureFlags[flagName])}
                      onCheckedChange={(checked) => {
                        setFormState((current) => (
                          current
                            ? {
                                ...current,
                                featureFlags: {
                                  ...current.featureFlags,
                                  [flagName]: checked,
                                },
                              }
                            : current
                        ));
                      }}
                      disabled={mutation.isPending}
                    />
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <Button
              type="submit"
              disabled={mutation.isPending || !isDirty}
              size="sm"
            >
              <Save className="mr-1.5 h-4 w-4" />
              {mutation.isPending ? 'Saving...' : 'Save Legacy Config'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </form>
  );
}

// =============================================================================
// User Groups Card
// =============================================================================

function UsergroupsCard({ accountId }: { accountId: string }) {
  const queryClient = useQueryClient();

  const { data: account } = useQuery({
    queryKey: ['account', accountId],
    queryFn: () => getAccount(accountId, { includeArchived: true }),
  });

  const { data: accountGroups, isLoading: loadingAccount } = useQuery({
    queryKey: ['account-usergroups', accountId],
    queryFn: () => getAccountUsergroups(accountId),
  });

  const { data: allGroups, isLoading: loadingAll } = useQuery({
    queryKey: ['all-usergroups'],
    queryFn: () => getAllUsergroups(),
  });

  const assignMutation = useMutation({
    mutationFn: (usergroupIds: string[]) =>
      assignAccountUsergroups(accountId, usergroupIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-usergroups', accountId] });
      queryClient.invalidateQueries({ queryKey: ['all-usergroups'] });
      queryClient.invalidateQueries({ queryKey: ['account-users', accountId] });
      toast.success('User groups updated');
    },
    onError: () => {
      toast.error('Failed to update user groups');
    },
  });

  const selectedIds = useMemo(
    () => (accountGroups || []).map((g) => g.id),
    [accountGroups]
  );

  const currentOrgId = account?.organisation_id;

  // Filter usergroups to only those belonging to the selected organisation
  const filteredGroups = useMemo(() => {
    if (!allGroups || !currentOrgId) return [];
    return allGroups.filter((g) => g.organisationId === currentOrgId);
  }, [allGroups, currentOrgId]);

  const isLoading = loadingAccount || loadingAll;

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-32" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">User Groups</CardTitle>
      </CardHeader>
      <CardContent>
        {!currentOrgId ? (
          <p className="text-sm text-muted-foreground">
            Select an organisation first to see available user groups.
          </p>
        ) : (
          <UsergroupMultiSelect
            allGroups={filteredGroups}
            selectedIds={selectedIds}
            accountId={accountId}
            onChange={(nextSelectedIds) => {
              assignMutation.mutate(nextSelectedIds);
            }}
            disabled={assignMutation.isPending}
          />
        )}
      </CardContent>
    </Card>
  );
}

function UsergroupMultiSelect({
  allGroups,
  selectedIds,
  accountId,
  onChange,
  disabled,
}: {
  allGroups: AccountUsergroup[];
  selectedIds: string[];
  accountId: string;
  onChange: (nextSelectedIds: string[]) => void;
  disabled: boolean;
}) {
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  return (
    <MultiSelectPopover
      value={selectedIds}
      options={allGroups.map((group) => ({
        id: group.id,
        label: group.name,
        secondaryLabel:
          group.accountId && group.accountId !== accountId && !selectedSet.has(group.id)
            ? group.accountName || undefined
            : undefined,
      }))}
      onChange={onChange}
      placeholder="Select user groups..."
      searchPlaceholder="Search user groups..."
      emptyMessage="No user groups found"
      noMatchesMessage="No matching groups"
      showSelectAll
      selectAllLabel="Select all"
      disabled={disabled}
    />
  );
}

// =============================================================================
// Organisation Card
// =============================================================================

function OrganisationCard({ accountId }: { accountId: string }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const { data: account } = useQuery({
    queryKey: ['account', accountId],
    queryFn: () => getAccount(accountId, { includeArchived: true }),
  });

  const { data: orgs, isLoading: searchLoading } = useQuery({
    queryKey: ['organisations-search', 'customer', debouncedSearch],
    queryFn: () => searchOrganisations(debouncedSearch, { type: 'customer' }),
    enabled: open,
  });

  const mutation = useMutation({
    mutationFn: (orgId: string | null) =>
      updateAccountSettings(accountId, { organisationId: orgId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account', accountId] });
      queryClient.invalidateQueries({ queryKey: ['account-dashboard', accountId] });
      queryClient.invalidateQueries({ queryKey: ['account-users', accountId] });
      toast.success('Account updated');
      setOpen(false);
    },
    onError: () => {
      toast.error('Failed to update account');
    },
  });

  const currentOrgId = account?.organisation_id;
  const selectedOrg = orgs?.find((org) => org.id === currentOrgId);
  const selectedOrgLabel = account?.organisation_name
    ? formatOrganisationLabel(
        account.organisation_name,
        account.organisation_number ?? selectedOrg?.accountNumber
      )
    : 'No account assigned';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Legacy Organisation</CardTitle>
      </CardHeader>
      <CardContent>
        <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setSearch(''); }}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="w-full justify-between font-normal h-auto min-h-9"
              disabled={mutation.isPending}
            >
              <span className={currentOrgId ? '' : 'text-muted-foreground'}>
                {selectedOrgLabel}
              </span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
            <div className="p-2 border-b">
              <div className="flex items-center gap-2 px-1">
                <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <input
                  placeholder="Search accounts..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                />
                {search && (
                  <button
                    onClick={() => setSearch('')}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
            <div className="max-h-56 overflow-y-auto p-1">
              {currentOrgId && (
                <button
                  className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-accent cursor-pointer text-muted-foreground"
                  onClick={() => mutation.mutate(null)}
                >
                  Remove assignment
                </button>
              )}
              {searchLoading ? (
                <div className="px-2 py-3 text-xs text-muted-foreground text-center">
                  Searching...
                </div>
              ) : orgs && orgs.length > 0 ? (
                orgs.map((org) => (
                  <button
                    key={org.id}
                    className={`w-full flex items-center justify-between px-2 py-1.5 text-sm rounded hover:bg-accent cursor-pointer ${
                      org.id === currentOrgId ? 'bg-accent' : ''
                    }`}
                    onClick={() => mutation.mutate(org.id)}
                  >
                    <span className="truncate">
                      {formatOrganisationLabel(org.name, org.accountNumber)}
                    </span>
                  </button>
                ))
              ) : (
                <div className="px-2 py-3 text-xs text-muted-foreground text-center">
                  {debouncedSearch ? 'No matching accounts' : 'Type to search...'}
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>
        <p className="text-xs text-muted-foreground mt-1.5">
          Link this account to a MongoDB organisation
        </p>
      </CardContent>
    </Card>
  );
}
